import { CroppingStyleMode } from "@omnia/shared/crops";
import { ExpandButton } from "@omnia/shared/react/buttons/ExpandButton";
import { Checkbox } from "@omnia/shared/react/checkbox";
import { classes } from "@omnia/shared/react/react";
import { PropsWithUpdate } from "@omnia/shared/react/reactDirective";
import { useState } from "react";
import { CroppingLayer, CroppingLayerLabelSettings } from "./croppingLayer";
import { FormGroupSelect } from "@omnia/shared/react/Select";

export type CroppingLayerOptionsProps = {
    layer: CroppingLayer;
};

const LABEL_LABELS: Record<keyof CroppingLayerLabelSettings, string> = {
    fieldName: "Field Name",
    fieldNumber: "Field Number",
    cropName: "Crop Name",
    varietyName: "Variety Name",
    croppingDescription: "Cropping Description",
    plotId: "Plot ID",
    sfiManagementOptions: "DEFAULT management options",
    area: "Area",
};

const COLOUR_MODE_LABELS: Record<CroppingStyleMode, string> = {
    crop: "Crop",
    variety: "Variety",
    purposeAndGrass: "Purpose",
    description: "Description",
};

export function CroppingLayerOptions({ layer, update }: PropsWithUpdate<CroppingLayerOptionsProps>) {
    const cn = (...args: string[]) => ["cropping-layer-options", ...args].join("--");

    const [expanded, setExpanded] = useState<boolean>(false);

    const labelOptions = Object.entries(LABEL_LABELS)
        .map((x) => x as [keyof CroppingLayerLabelSettings, string])
        .map(([key, label]) => ({
            checked: layer.showLabel(key),
            setChecked: (value: boolean) =>
                update(() => {
                    layer.showLabel(key, value);
                }),
            key,
            label: key === "sfiManagementOptions" ? layer.managementOptions?.title : label,
        }))
        .filter((x) => x.key !== "sfiManagementOptions" || layer.managementOptions?.enabled);

    const someLabelsEnabled = labelOptions.some((x) => x.checked);
    const someLabelsDisabled = labelOptions.some((x) => !x.checked);
    const onToggleAllLabels = (value: boolean) => labelOptions.forEach((x) => x.setChecked(value));

    const onToggleExpand = () =>
        update(() => {
            setExpanded((x) => {
                const next = !x;
                if (next) {
                    layer.visible = true;
                }
                return next;
            });
        });

    return (
        <div className={cn()}>
            <div className={classes(cn("heading"), { expanded: expanded })}>
                <span className={cn("heading-label")}>
                    <Checkbox
                        checked={layer.visible}
                        setChecked={(value) =>
                            update(() => {
                                layer.visible = value;
                                if (!value) {
                                    setExpanded(false);
                                }
                            })
                        }
                        disabled={layer.alwaysVisible}
                    >
                        {layer.description}
                    </Checkbox>

                    <MapLabelButton enabled={someLabelsEnabled} onToggle={onToggleAllLabels} />
                </span>

                <ExpandButton
                    className="cropping-layer-options--expand-button"
                    expanded={expanded}
                    onToggle={onToggleExpand}
                    iconExpanded="fas fa-chevron-up"
                    iconCollapsed="fas fa-chevron-down"
                />
            </div>

            {layer.visible && expanded ? (
                <div className="subsection">
                    <FormGroupSelect
                        label="Styles"
                        options={Object.entries(COLOUR_MODE_LABELS)}
                        value={layer.croppingStyleMode}
                        setValue={(value) =>
                            update(() => {
                                layer.croppingStyleMode = value;
                            })
                        }
                        disabled={!layer.visible}
                    />

                    <div className="form-group">
                        <label>
                            <Checkbox
                                checked={someLabelsEnabled}
                                indeterminate={someLabelsDisabled}
                                setChecked={onToggleAllLabels}
                            >
                                Labels
                            </Checkbox>
                        </label>
                        <ul className={cn("list")} style={{ padding: 0 }}>
                            {labelOptions.map(({ key, label, checked, setChecked }) => (
                                <li key={key} className={cn("list-item")}>
                                    <Checkbox checked={checked} setChecked={setChecked} disabled={!layer.visible}>
                                        {label}
                                    </Checkbox>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            ) : null}
        </div>
    );
}

type MapLabelButtonProps = {
    className?: string;
    enabled: boolean;
    onToggle: (value: boolean) => void;
};

function MapLabelButton({ className, enabled, onToggle }: MapLabelButtonProps) {
    const cn = (...args: string[]) => ["cropping-layer-options--map-label-button", ...args].join("--");
    return (
        <button
            type="button"
            className={classes(className, cn(), "btn btn-link btn-floating-action small", { [cn("disabled")]: !enabled })}
            title={enabled ? "Hide labels" : "Show labels"}
            onClick={() => onToggle(!enabled)}
        >
            <span>A</span>
        </button>
    );
}
